import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, IconButton, Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as CardIcon,
  Info as InfoIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { customerCardService } from '../../services/accounting/customerCard.service';
import { serverMessage } from '../../utils/apiConvert';
import CustomerCardDialog from '../../components/cards/CustomerCardDialog';
import CardFace, { isCardExpired } from '../../components/cards/CardFace';
import CardTransactionsDialog from '../../components/cards/CardTransactionsDialog';

const FS = '0.8125rem';

const PortalCards = ({ portalUser }) => {
  const canEdit = portalUser?.role === 'ORG_ADMIN';

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog state: null closed, { card } add (card null) or edit
  const [dialog, setDialog] = useState(null);

  // The card whose payments are shown
  const [txCard, setTxCard] = useState(null);

  // Remove confirmation
  const [removeId, setRemoveId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadCards = async () => {
    setLoading(true);
    try {
      setCards(await customerCardService.getCards());
    } catch (e) {
      setError(serverMessage(e, 'Failed to load cards'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCards(); }, []);

  const handleSave = async (body) => {
    setSaving(true);
    setError(null);
    try {
      if (!dialog.card) {
        const card = await customerCardService.createCard(body);
        setCards((prev) => [...prev, card]);
      } else {
        const card = await customerCardService.updateCard(dialog.card.id, body);
        setCards((prev) => prev.map((c) => (c.id === dialog.card.id ? card : c)));
      }
      setDialog(null);
    } catch (e) {
      setError(serverMessage(e, 'Failed to save the card'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    try {
      await customerCardService.removeCard(removeId);
      setCards((prev) => prev.filter((c) => c.id !== removeId));
    } catch (e) {
      setError(serverMessage(e, 'Failed to remove the card'));
    }
    setRemoveId(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      <Box>
        {/* Toolbar: count + Add Card (the page title is in the top bar) */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap', minHeight: 32 }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mr: 'auto' }}>
            {cards.length} {cards.length === 1 ? 'card' : 'cards'}
          </Typography>
          {canEdit && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDialog({ card: null })} sx={{ textTransform: 'none', fontSize: FS }}>
              Add Card
            </Button>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {cards.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CardIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: FS }}>No saved cards yet</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: canEdit ? 2 : 0 }}>
              {canEdit ? 'Add a card to help your typing center track payment sources.' : 'Your organisation has not added any cards.'}
            </Typography>
            {canEdit && (
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDialog({ card: null })} sx={{ textTransform: 'none', fontSize: FS }}>
                Add Card
              </Button>
            )}
          </Paper>
        )}

        {cards.length > 0 && (
          <Grid container spacing={1.5}>
            {cards.map((card) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={card.id}>
                <Box sx={{ maxWidth: 280 }}>
                  <CardFace card={card} expired={isCardExpired(card.expiry)} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, px: 0.25 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: FS }} noWrap title={card.nickname}>{card.nickname}</Typography>
                      {isCardExpired(card.expiry) && (
                        <Typography sx={{ fontSize: '0.6875rem', color: 'error.main' }}>Expired — update the expiry or add the new card</Typography>
                      )}
                    </Box>
                    <Tooltip title="Transactions">
                      <IconButton size="small" onClick={() => setTxCard(card)} aria-label="Card transactions"><HistoryIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {canEdit && (<>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => setDialog({ card })}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={() => setRemoveId(card.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </>)}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {cards.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 2, px: 0.5 }}>
            <InfoIcon sx={{ fontSize: 16, color: 'text.disabled', mt: 0.3 }} />
            <Typography variant="caption" color="text.secondary">
              We only store the card type, last 4 digits, bank, cardholder and nickname. We never store your full card number or CVV.
            </Typography>
          </Box>
        )}
      </Box>

      <CustomerCardDialog
        open={!!dialog}
        card={dialog?.card || null}
        saving={saving}
        onClose={() => setDialog(null)}
        onSave={handleSave}
      />

      <CardTransactionsDialog
        open={!!txCard}
        card={txCard}
        onClose={() => setTxCard(null)}
        load={customerCardService.getTransactions}
        documentPath={(t) => (t.documentType === 'INVOICE' ? `/portal/invoices/${t.documentId}` : `/portal/work-orders/${t.documentId}`)}
      />

      {/* ── Remove Confirmation ──────────────────────────────────── */}
      <Dialog open={!!removeId} onClose={() => setRemoveId(null)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>Remove Card</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: FS }}>
            Are you sure you want to remove this card? This won't affect any past transactions.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveId(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRemove} sx={{ textTransform: 'none' }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalCards;
